import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import Lightbox from "../components/Lightbox";
import { GALLERY, GALLERY_CATEGORIES, resolveImage } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const [items, setItems] = useState(GALLERY);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((res) => {
        if (Array.isArray(res.data?.gallery) && res.data.gallery.length > 0) {
          setItems(res.data.gallery);
        }
      })
      .catch(() => {});
  }, []);

  const visible = useMemo(
    () => (filter === "all" ? items : items.filter((g) => g.category === filter)),
    [items, filter]
  );

  const move = (dir) =>
    setActive((a) => (a === null ? null : (a + dir + visible.length) % visible.length));

  return (
    <main data-testid="gallery-page">
      <Seo
        title="Gallery | The Jamoon, Corbett"
        description="Explore photographs of The Jamoon, its forest setting, the Ramganga, wildlife, accommodation and experiences in Corbett."
        path="/gallery"
        image={resolveImage(GALLERY[0].uri, 1200, 630)}
        imageAlt="Tiger captured by a camera trap at The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Gallery", path: "/gallery" }]}
      />
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-36 sm:px-12 sm:pt-44 lg:px-20">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Gallery</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight text-ivory sm:text-5xl">
            The Jamoon <span className="italic text-brass">Gallery</span>
          </h1>
        </Reveal>

        <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Gallery categories">
          {[{ key: "all", label: "All" }, ...GALLERY_CATEGORIES].map((c) => (
            <button
              key={c.key}
              data-testid={`gallery-filter-${c.key}`}
              onClick={() => { setFilter(c.key); setActive(null); }}
              role="tab"
              aria-selected={filter === c.key}
              className={`border px-5 py-2.5 text-[11px] font-medium tracking-[0.22em] transition-colors duration-300 ${
                filter === c.key
                  ? "border-brass bg-brass text-charcoal"
                  : "border-ivory/20 text-ivory/70 hover:border-brass hover:text-brass"
              }`}
            >
              {c.label.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-3">
          {visible.map((g, i) => (
            <motion.button
              key={g.uri || g.image || i}
              data-testid={`gallery-page-item-${i}`}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setActive(i)}
              className="group block w-full overflow-hidden focus:outline-none focus:ring-1 focus:ring-brass"
              aria-label={`Open image: ${g.caption}`}
            >
              <img
                src={resolveImage(g.uri || g.image, 800, 800)}
                alt={g.caption}
                loading="lazy"
                className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </motion.button>
          ))}
        </div>
      </section>

      <Lightbox items={visible} index={active} onClose={() => setActive(null)} onNavigate={move} />
    </main>
  );
}
